import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { useMatch } from 'react-router-dom';
import { v2, type NotificationApi } from '@/api/v2Client';
import { useNotificationsStream } from '@/hooks/useNotificationsStream';
import {
  refreshNotificationCount,
  setNotificationCount,
  incrementNotificationCount,
  decrementNotificationCount,
  useNotificationCount,
} from '@/hooks/useNotificationCount';
import { NotificationsDropdown } from './NotificationsDropdown';

// Phase 5.X-notifications-system — header bell icon + dropdown.
//
// State:
//   - unreadCount: number — driven by the shared useNotificationCount store.
//     Cold-start via refreshNotificationCount() on mount; live updates via
//     SSE (incrementNotificationCount) and 60-second polling.
//   - recent: NotificationApi[] — most recent rows; populated lazily on
//     first dropdown open AND from SSE pushes. The full /notifications
//     page is the canonical surface for browsing history.
//   - open: boolean — dropdown visibility.
//
// Connection lifecycle:
//   - Cold load: refreshNotificationCount() on mount (shared store).
//   - Poll: re-fetch every 60 s so stale badges (e.g. another tab marks
//     notifications read) self-correct.
//   - SSE stream: opens on mount via useNotificationsStream; each push
//     bumps the shared store + prepends to recent.
//   - Click bell: toggles open. On first open, fetch the unread list
//     (cap 20) into `recent`.
//   - Outside-click: closes the dropdown via a window-level mousedown
//     listener that ignores clicks inside the bell container.
//   - bell:invalidate event: dispatched by NotificationsPage on mount so
//     the badge refreshes immediately when the user navigates there.

const POLL_INTERVAL_MS = 60_000;

const CONTAINER_STYLE: CSSProperties = {
  position: 'relative',
};

const BELL_BTN_STYLE: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 20,
  color: 'var(--primary)',
  padding: 8,
  width: 40,
  height: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  lineHeight: 1,
  position: 'relative',
};

const BADGE_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  background: '#dc2626',
  color: '#fff',
  borderRadius: 10,
  padding: '0 6px',
  fontSize: 10,
  fontWeight: 600,
  lineHeight: '16px',
  minWidth: 16,
  textAlign: 'center',
};

export function BellIcon({ onOpen }: { onOpen?: () => void } = {}): ReactElement {
  const match = useMatch('/submissions/:submissionId/*');
  const submissionId = match?.params.submissionId;
  const currentSubmissionId = submissionId && submissionId !== 'new' ? submissionId : undefined;
  const unreadCount = useNotificationCount();
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<NotificationApi[]>([]);
  const [recentLoaded, setRecentLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Cold-start + 60-second polling + bell:invalidate listener.
  useEffect(() => {
    refreshNotificationCount();

    const intervalId = globalThis.setInterval(refreshNotificationCount, POLL_INTERVAL_MS);

    const onInvalidate = (): void => refreshNotificationCount();
    window.addEventListener('bell:invalidate', onInvalidate);

    return () => {
      globalThis.clearInterval(intervalId);
      window.removeEventListener('bell:invalidate', onInvalidate);
    };
  }, []);

  // SSE — append incoming notifications to recent + bump shared count.
  useNotificationsStream({
    onNotification: (n) => {
      setRecent((prev) => {
        if (prev.some((p) => p.id === n.id)) return prev;
        return [n, ...prev].slice(0, 20);
      });
      if (n.readAt === null) incrementNotificationCount();
    },
  });

  // Outside-click → close dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: globalThis.MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as globalThis.Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // First-open lazy load of recent unread.
  const handleToggle = useCallback(async () => {
    if (open) {
      setOpen(false);
      return;
    }
    onOpen?.();
    setOpen(true);
    if (recentLoaded) return;
    try {
      const res = await v2.notifications.list({ limit: 20 });
      setRecent(res.notifications);
      setRecentLoaded(true);
    } catch {
      // Leave recent empty; the dropdown will show the empty state.
    }
  }, [open, recentLoaded, onOpen]);

  const handleItemClick = useCallback(async (n: NotificationApi) => {
    if (n.readAt !== null) return; // already read — no-op
    setRecent((prev) =>
      prev.map((p) => (p.id === n.id ? { ...p, readAt: new Date().toISOString() } : p)),
    );
    decrementNotificationCount();
    try {
      await v2.notifications.markRead(n.id);
    } catch {
      setRecent((prev) =>
        prev.map((p) => (p.id === n.id ? { ...p, readAt: null } : p)),
      );
      incrementNotificationCount();
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    const snapshot = recent;
    const snapshotCount = unreadCount;
    setNotificationCount(0);
    setRecent((prev) =>
      prev.map((p) =>
        p.readAt === null ? { ...p, readAt: new Date().toISOString() } : p,
      ),
    );
    try {
      await v2.notifications.markAllRead();
    } catch {
      setRecent(snapshot);
      setNotificationCount(snapshotCount);
    }
  }, [recent, unreadCount]);

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);
  return (
    <div ref={containerRef} style={CONTAINER_STYLE} data-testid="bell-container">
      <button
        type="button"
        ref={buttonRef}
        aria-label={`Notifications (${unreadCount} unread)`}
        data-testid="bell-button"
        title="Notifications"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => void handleToggle()}
        style={BELL_BTN_STYLE}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path d="M10 21h4" />
        </svg>
        {unreadCount > 0 && (
          <span data-testid="bell-badge" style={BADGE_STYLE}>
            {badgeText}
          </span>
        )}
      </button>
      {open && (
        <NotificationsDropdown
          notifications={recent}
          {...(currentSubmissionId && { currentSubmissionId })}
          unreadCount={unreadCount}
          onItemClick={(n) => void handleItemClick(n)}
          onMarkAllRead={() => void handleMarkAllRead()}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
